/**
 * `IconButton` (W1-12 ui-kit-i18n; contract IconButtonProps): a square icon-only button. `label` is its
 * accessible name and tooltip; with `command` the tooltip appends the shortcut, a click runs the command through
 * the UiBridge unless the caller gave `onClick` (a handler merged in by a Radix trigger such as Popover does not
 * count, see internal/commandButton.ts) and the registry's disabled reason applies. Same focus and disabled
 * rules as Button (no focus on pointer down, aria-disabled with the reason in the tooltip). `pressed` makes it a
 * toggle button (aria-pressed). Wrapped in `<Tooltip>`, its own tooltip shows that content instead of the label.
 */

import { useId, type MouseEvent as ReactMouseEvent } from 'react';
import { commandDisabledReason, useTr, useUiBridge } from './bridge';
import s from './Button.module.css';
import { Icon } from './Icon';
import type { NativeButtonProps } from './internal/buttonProps';
import { registerCommandButton, runsCommandOnClick, type CommandButtonPrivateProps } from './internal/commandButton';
import { cx } from './internal/cx';
import { keepFocusOnPointerDown } from './internal/focus';
import { Tooltip } from './Tooltip';
import type { IconButtonProps } from './types';

export type IconButtonAllProps = Omit<IconButtonProps, 'onClick'> &
  NativeButtonProps<IconButtonProps & CommandButtonPrivateProps> &
  CommandButtonPrivateProps & {
    /** Tooltip side (default 'top'; tool rails use 'right'). */
    readonly tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  };

export function IconButton(props: IconButtonAllProps) {
  const {
    icon,
    label,
    command,
    pressed,
    disabledReason,
    size = 'md',
    helpId,
    tooltipSide,
    onClick,
    callerOnClick,
    tooltip,
    onMouseDown,
    className,
    ...rest
  } = props;
  const bridge = useUiBridge();
  const tr = useTr();
  const reasonId = useId();
  const reason = disabledReason ?? commandDisabledReason(bridge, command);
  const disabled = reason !== null;
  const name = tr(label);
  const reasonText = reason === null ? null : tr(reason);

  const handleClick = (e: ReactMouseEvent<HTMLButtonElement>): void => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
    if (runsCommandOnClick(command, onClick, callerOnClick)) bridge.run(command);
  };
  const handleMouseDown = (e: ReactMouseEvent<HTMLButtonElement>): void => {
    onMouseDown?.(e);
    keepFocusOnPointerDown(e);
  };
  const describedBy = [rest['aria-describedby'], disabled ? reasonId : undefined].filter(Boolean).join(' ');

  return (
    <>
      <Tooltip
        content={tooltip === undefined ? name : tooltip.content}
        command={tooltip?.command ?? command}
        detail={reasonText ?? tooltip?.detail}
        side={tooltip?.side ?? tooltipSide ?? 'top'}
        open={tooltip?.open}
        onOpenChange={tooltip?.onOpenChange}
      >
        <button
          {...rest}
          type="button"
          className={cx(s.iconButton, size === 'sm' && s.sm, disabled && s.disabled, className)}
          aria-label={name}
          aria-pressed={pressed}
          aria-disabled={disabled ? true : undefined}
          aria-describedby={describedBy === '' ? undefined : describedBy}
          data-help-id={helpId}
          onClick={handleClick}
          onMouseDown={handleMouseDown}
        >
          <Icon name={icon} size={size === 'sm' ? 14 : 16} />
        </button>
      </Tooltip>
      {reasonText !== null && (
        <span id={reasonId} hidden>
          {reasonText}
        </span>
      )}
    </>
  );
}

registerCommandButton(IconButton);
